import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { FILTERED_GREEN_BUTTON } from '../constants/colors';
import { PRAIRI_SURFACES } from '../constants/prairiSurfaces';

interface DashboardWelcomeCalloutProps {
  onDismiss: () => void;
}

/** Tail tip sits this far in from the bubble's left edge. */
const TAIL_LEFT = 18;

function calloutPosition(bubbleHeight: number): { left: number; top: number } {
  const height = bubbleHeight > 0 ? bubbleHeight : 112;
  const width = Math.min(416, window.innerWidth - 32);
  const maxLeft = Math.max(16, window.innerWidth - width - 16);
  const sfy = document.querySelector('[data-catalog-sfy-tab]');
  if (!sfy) return { left: 24, top: 96 };
  const rect = sfy.getBoundingClientRect();
  if (rect.width <= 0 || rect.height <= 0) return { left: 24, top: 96 };

  // Document coordinates, so the bubble scrolls with the page instead of tracking the viewport.
  // The tail tip sits on the end of the category name ("you"). The body stands to the right of that.
  const label = sfy.querySelector('h2');
  const labelRect = label?.getBoundingClientRect();
  const tipX =
    labelRect && labelRect.width > 0 ? labelRect.right + 8 : rect.left + rect.width * 0.8;
  return {
    left: Math.min(Math.max(16, tipX - TAIL_LEFT), maxLeft) + window.scrollX,
    top: rect.top + window.scrollY - height - 30,
  };
}

/**
 * First-visit note on the investor dashboard. It sits in line with Specially for you.
 */
const DashboardWelcomeCallout: React.FC<DashboardWelcomeCalloutProps> = ({ onDismiss }) => {
  const [position, setPosition] = useState(() => calloutPosition(0));

  useEffect(() => {
    const update = () => {
      const bubble = document.querySelector(
        '[aria-label="Welcome to your dashboard"]'
      ) as HTMLElement | null;
      setPosition(calloutPosition(bubble?.getBoundingClientRect().height ?? 0));
    };
    update();
    // The featured card arrives with the catalog, after this tip has already mounted.
    const frame = window.requestAnimationFrame(update);
    const timer = window.setInterval(update, 250);
    const stop = window.setTimeout(() => window.clearInterval(timer), 5000);
    window.addEventListener('resize', update);
    return () => {
      window.cancelAnimationFrame(frame);
      window.clearInterval(timer);
      window.clearTimeout(stop);
      window.removeEventListener('resize', update);
    };
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onDismiss();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onDismiss]);

  return createPortal(
    <>
      <div className="fixed inset-0 z-[1000]" onClick={onDismiss} aria-hidden />
      <div
        role="dialog"
        aria-label="Welcome to your dashboard"
        className="absolute z-[1001] w-[min(26rem,calc(100vw-2rem))]"
        style={{
          left: position.left,
          top: position.top,
        }}
        onClick={event => event.stopPropagation()}
      >
        <div
          className="relative rounded-2xl bg-white px-5 py-3.5 shadow-xl"
          style={{ color: PRAIRI_SURFACES.page, fontFamily: 'Rubik, sans-serif' }}
        >
          <span
            aria-hidden
            className="pointer-events-none absolute bg-white"
            style={{
              left: TAIL_LEFT,
              bottom: -22,
              width: 44,
              height: 24,
              clipPath: 'polygon(0 0, 100% 0, 0 100%)',
            }}
          />
          <div className="relative">
            <p className="text-[18px] leading-snug">
              Your personalized deal flow, based on your investment preferences.
            </p>
            <div className="mt-2 flex justify-end">
              <button
                type="button"
                onClick={onDismiss}
                className={`rounded-full px-5 py-1 text-[15px] font-medium ${FILTERED_GREEN_BUTTON}`}
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
};

export default DashboardWelcomeCallout;
